// packages/web/src/lib/recordEditor.ts — R3: the tiny app-wide store that lets
// ANY screen open the schema-driven record editor (RecordForm + RecordActions)
// without threading callbacks through the whole shell, and lets every read
// surface refresh itself the moment a record is created/edited/acted-on/deleted.
//
// Two doors to the engine, one place:
//   • openRecordEditor({mode:'create'|'edit', …}) — mounts the overlay host
//     (components/RecordEditor.tsx) with a create form OR an edit form + the
//     generic actions (transitions / status / delete) for that record.
//   • openCreateMenu() — the GLOBAL create menu (every creatable type), so
//     nothing is un-creatable even where a screen offers no bespoke "+ New".
//
// A mutation calls bumpRecordsVersion(); screens read useRecordsVersion() in
// their load effect's deps, so a create/edit/act/delete anywhere re-runs the
// underlying read (no manual prop wiring, no stale list). Module-level state +
// a subscriber set is the whole mechanism — deliberately smaller than pulling
// in a context/store lib for what is one boolean, one request, one counter.
import { useEffect, useState } from 'preact/hooks';

/** A request to open the record editor overlay. `prefillFields` seeds a create
 * form's field values; `prefillRels` seeds its relationship selections (keyed
 * by relationship key) — how a sub-record created from its parent detail
 * pre-points at that parent (e.g. a new payment pre-linked to its booking). */
export type EditorRequest =
  | {
      mode: 'create';
      typeKey: string;
      prefillFields?: Record<string, unknown>;
      prefillRels?: Record<string, string[]>;
    }
  | { mode: 'edit'; typeKey: string; id: string };

type Listener = () => void;

let currentRequest: EditorRequest | null = null;
let createMenuOpen = false;
let recordsVersion = 0;

const requestListeners = new Set<Listener>();
const menuListeners = new Set<Listener>();
const versionListeners = new Set<Listener>();

function emit(listeners: Set<Listener>): void {
  for (const listener of listeners) listener();
}

export function openRecordEditor(request: EditorRequest): void {
  currentRequest = request;
  // Opening the editor always dismisses the create menu behind it.
  if (createMenuOpen) {
    createMenuOpen = false;
    emit(menuListeners);
  }
  emit(requestListeners);
}

export function closeRecordEditor(): void {
  if (currentRequest === null) return;
  currentRequest = null;
  emit(requestListeners);
}

export function openCreateMenu(): void {
  createMenuOpen = true;
  emit(menuListeners);
}

export function closeCreateMenu(): void {
  if (!createMenuOpen) return;
  createMenuOpen = false;
  emit(menuListeners);
}

/** Signals that a record changed (create/edit/transition/status/delete). Read
 * surfaces include useRecordsVersion() in their load effect deps to reload. */
export function bumpRecordsVersion(): void {
  recordsVersion += 1;
  emit(versionListeners);
}

function useStore<T>(read: () => T, listeners: Set<Listener>): T {
  const [value, setValue] = useState<T>(read);
  useEffect(() => {
    const listener = () => setValue(read());
    listeners.add(listener);
    // A change may have landed between the initial read and subscribe.
    listener();
    return () => {
      listeners.delete(listener);
    };
    // read/listeners are module-stable; intentionally run once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return value;
}

export function useRecordEditorRequest(): EditorRequest | null {
  return useStore(() => currentRequest, requestListeners);
}

export function useCreateMenuOpen(): boolean {
  return useStore(() => createMenuOpen, menuListeners);
}

export function useRecordsVersion(): number {
  return useStore(() => recordsVersion, versionListeners);
}
